import React from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FaqSectionProps {
  title: string;
  content: string;
  sections: Array<{
    title: string;
    questions: Array<{
      question: string;
      answer: string;
    }>;
  }>;
  selectedCategory: 'all' | 'general' | 'canada' | 'other' | 'card';
  onCategoryChange: (category: 'all' | 'general' | 'canada' | 'other' | 'card') => void;
  expandedFaqSection: string | null;
  setExpandedFaqSection: (section: string | null) => void;
  expandedQuestion: string | null;
  setExpandedQuestion: (question: string | null) => void;
}

const categories = [
  { key: 'all' as const, label: 'Toutes' },
  { key: 'general' as const, label: 'Generales' },
  { key: 'canada' as const, label: 'Canada' },
  { key: 'other' as const, label: 'Autres destinations' },
  { key: 'card' as const, label: "Carte d'assure" },
];

export function FaqSection({
  sections,
  selectedCategory,
  onCategoryChange,
  expandedFaqSection,
  setExpandedFaqSection,
  expandedQuestion,
  setExpandedQuestion
}: FaqSectionProps) {
  return (
    <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800/50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-10"
        >
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Questions frequentes
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Retrouvez les reponses aux questions les plus frequentes sur Globe PVT
          </p>
        </motion.div>

        <div className="flex flex-wrap items-center justify-center gap-2 mb-8 px-1">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => onCategoryChange(cat.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 ${
                selectedCategory === cat.key
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {sections.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 overflow-hidden"
            >
              <button
                onClick={() => setExpandedFaqSection(expandedFaqSection === section.title ? null : section.title)}
                className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                    {section.title}
                  </h3>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 flex-shrink-0 ${
                  expandedFaqSection === section.title ? 'rotate-180' : ''
                }`} />
              </button>

              <AnimatePresence>
                {expandedFaqSection === section.title && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-2">
                      {section.questions.map((item, questionIndex) => {
                        const qKey = `${sectionIndex}-${questionIndex}`;
                        const isOpen = expandedQuestion === qKey;

                        return (
                          <div
                            key={questionIndex}
                            className={`rounded-lg border transition-all duration-200 ${
                              isOpen
                                ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10'
                                : 'border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-700/20'
                            }`}
                          >
                            <button
                              onClick={() => setExpandedQuestion(isOpen ? null : qKey)}
                              className="w-full flex items-start justify-between gap-3 p-3.5 text-left"
                            >
                              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {item.question}
                              </span>
                              <ChevronDown className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform duration-200 mt-0.5 flex-shrink-0 ${
                                isOpen ? 'rotate-180' : ''
                              }`} />
                            </button>

                            <AnimatePresence>
                              {isOpen && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-3.5 pb-3.5">
                                    <div
                                      className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 [&_a]:text-emerald-600 dark:[&_a]:text-emerald-400"
                                      dangerouslySetInnerHTML={{ __html: item.answer }}
                                    />
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
